import { EventEmitter2 } from 'eventemitter2';
import { debounce, get } from 'lodash';

import { generateWaBasketQuantityUpdate } from 'apps/analytics/utils/basketQuantityUpdateStatistics';
import {
  reportBasketQuantityUpdates,
  reportPageComponents,
  reportPageEvents,
  reportPageView,
} from '../utils/handlers';

import pageEventTypes from 'apps/analytics/utils/pageEventTypes';

// it's set to true for tests to silence console-logs when test uses network (alternative it to set redux store with pageViewId)
const silentDefaultMode = process.env.NODE_ENV === 'test';
const PAGE_EVENT_CALLBACK_MAX_WAIT_TIME = 1000; // ms

// Feel free to add triggerPageStats and triggerPageComponents methods if necessary
export class WAEventEmitter {
  constructor() {
    this.eventEmitter = new EventEmitter2();
    this.trigger = this.trigger.bind(this);
    this.silent = silentDefaultMode;

    this.addEventHandlers();
  }

  setSilent(silent) {
    this.silent = silent;
  }

  setStore(store) {
    this.store = store;
  }

  getPageViewId = () => {
    const state = this.store.getState();
    return get(state, 'analytics.pageView.id', '');
  };

  setPageType = (pageType) => { this.pageType = pageType; };

  trigger(...args) {
    try {
      this.eventEmitter.emit(...args);
    } catch (e) {
      if (!this.silent) {
        console.log('WA Event trigger error', e.message); // eslint-disable-line no-console
      }
    }
  }

  triggerBasketQuantityUpdateEvent(basketUpdateResponse, analyticsContextData) {
    this.trigger('wa:basketQuantityUpdate', basketUpdateResponse, analyticsContextData);
  }

  triggerPageEvent(type, fields, callback) {
    const eventData = { type };
    if (fields) {
      eventData.fields = fields;
    }
    this.trigger('wa:pageEvent', eventData, callback);
  }

  triggerPageEventWithDescription(description, fields, callback) {
    const eventData = { description };
    if (fields) {
      eventData.fields = fields;
    }
    this.trigger('wa:pageEvent', eventData, callback);
  }

  addEventHandlers() {
    this.eventEmitter.on('wa:pageView', (pageView, onPageViewCreated) => {
      const pvid = this.getPageViewId();
      if (pvid) {
        reportPageView(pvid, pageView, (viewData) => {
          if (pageView && pageView.pageType) {
            this.setPageType(pageView.pageType);
          }
          if (onPageViewCreated) {
            onPageViewCreated(viewData);
          }
        });
      } else if (!this.silent) {
        throw new Error('wa:pageView - no pageViewId');
      }
    });

    this.eventEmitter.on('wa:pageEvent', (pageEvent, callback) => {
      const pvid = this.getPageViewId();
      let callbackExecuted = false;
      let callbackWrapper;
      let callbackTimeout;

      if (typeof callback === 'function') {
        callbackWrapper = () => {
          if (!callbackExecuted) {
            clearTimeout(callbackTimeout);
            callbackExecuted = true;
            callback();
          }
        };
        callbackTimeout = setTimeout(callbackWrapper, PAGE_EVENT_CALLBACK_MAX_WAIT_TIME);
      }

      if (pvid) {
        reportPageEvents(pvid, [pageEvent]).then(() => {
          if (typeof callbackWrapper === 'function') {
            callbackWrapper();
          }
        });
      } else if (!this.silent) {
        throw new Error('wa:pageEvent - no pageViewId');
      }
    });

    this.eventEmitter.on('wa:pageComponents', ({ pageView, pageComponents }) => {
      const pvid = this.getPageViewId();
      if (pvid && pageView.id === pvid) {
        reportPageComponents(pvid, { pageComponents });
      } else if (!this.silent) {
        throw new Error('wa:pageComponents - no pageViewId');
      }
    });

    this.eventEmitter.on('wa:basketQuantityUpdate', (response, analyticsContextData) => {
      const pvid = this.getPageViewId();
      if (pvid) {
        const basketQuantityUpdates = generateWaBasketQuantityUpdate(response, analyticsContextData, this.pageType);
        reportBasketQuantityUpdates(pvid, basketQuantityUpdates);
      } else if (!this.silent) {
        throw new Error('wa:basketQuantityUpdate - no pageViewId');
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('resize',
        debounce((triggerPageEvent => {
          let prevWindowWidth = window.innerWidth;
          return () => {
            const currWindowWidth = window.innerWidth;
            // From old webshop code:
            // Only the width changes are meaningful.
            // Height can change for example when browser menu appears.
            if (currWindowWidth === prevWindowWidth) {
              return;
            }

            prevWindowWidth = currWindowWidth;
            const windowSize = {
              VIEWPORT_HEIGHT: window.innerHeight,
              VIEWPORT_WIDTH: currWindowWidth,
            };
            triggerPageEvent(pageEventTypes.WINDOW_RESIZE, windowSize);
          };
        })(this.triggerPageEvent.bind(this)), 1000)
      );
    }
  }
}

// export as singleton
const waEventEmitter = new WAEventEmitter();

export default waEventEmitter;
